import React from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { DeleteOutline, Replay, Undo } from '@mui/icons-material';
import { SimTrackUsed } from '../services/api';

const RELATION_LABELS: Record<SimTrackUsed['relation'], string> = {
  same_track: 'Selected track',
  similar: 'Similar track',
  same_type: 'Same type',
  other: 'Other',
};

const formatPct = (p: number): string => {
  const pct = p * 100;
  if (pct > 0 && pct < 0.1) return '<0.1%';
  return `${pct.toFixed(1)}%`;
};

const sameSet = (a: Set<string>, b: string[]) => a.size === b.length && b.every((k) => a.has(k));

export interface TracksUsedPanelProps {
  tracks: SimTrackUsed[];
  /** Track keys removed in the run currently shown. */
  appliedExcluded: string[];
  /** Track keys the user has marked for removal (applied on the next run). */
  pendingExcluded: Set<string>;
  onToggle: (trackKey: string) => void;
  onRerun: () => void;
  onReset: () => void;
  isRunning: boolean;
  /** Two-layer model: these are the form tracks; linked ones also feed the track-specific adjustment. */
  twoLayer?: { linkedTrackKeys: Set<string>; family: string };
  /** Drops the panel's own Paper framing/padding when it's rendered inside another card (e.g. a tab). */
  embedded?: boolean;
}

/** "Tracks feeding this simulation" — which tracks the ratings are built from, and how much each counts. */
export const TracksUsedPanel: React.FC<TracksUsedPanelProps> = ({
  tracks,
  appliedExcluded,
  pendingExcluded,
  onToggle,
  onRerun,
  onReset,
  isRunning,
  twoLayer,
  embedded = false,
}) => {
  const dirty = !sameSet(pendingExcluded, appliedExcluded);
  const nKept = tracks.filter((t) => !pendingExcluded.has(t.trackKey)).length
    + appliedExcluded.filter((k) => !pendingExcluded.has(k)).length;
  const Frame: React.ElementType = embedded ? Box : Paper;
  return (
    <Frame sx={{ p: embedded ? 0 : 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        {twoLayer ? 'Form tracks' : 'Tracks feeding this simulation'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {twoLayer
          ? `Layer 1 (form) uses every ${twoLayer.family} at equal weight; each track's share reflects how many races it had and how recent they were. Tracks marked "Linked" also feed the track-specific adjustment. Hover a track to remove it from form, then re-run.`
          : "Driver ratings are built only from these tracks. Similar tracks are ranked by how alike their results are to the selected track's, and weighted by that score and by how recent each race was. Hover a track to remove it, then re-run."}
      </Typography>
      <Stack spacing={1.5}>
        {tracks.map((t) => {
          const removed = pendingExcluded.has(t.trackKey);
          const canRemove = t.relation !== 'same_track';
          return (
            <Box
              key={t.trackKey}
              sx={{
                position: 'relative',
                opacity: removed ? 0.45 : 1,
                // Show the remove button only on hover (always visible on touch devices).
                '& .track-action': { opacity: removed ? 1 : 0, transition: 'opacity 0.15s' },
                '&:hover .track-action': { opacity: 1 },
                '@media (hover: none)': { '& .track-action': { opacity: 1 } },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{ fontWeight: 600, textDecoration: removed ? 'line-through' : 'none' }}
                    noWrap
                  >
                    {t.trackKey}
                  </Typography>
                  {twoLayer?.linkedTrackKeys.has(t.trackKey) && !removed && (
                    <Tooltip title="Also feeds the track-specific adjustment">
                      <Chip size="small" label="Linked" color="primary" variant="outlined" />
                    </Tooltip>
                  )}
                  {/* Only the selected track (and removed tracks) get a chip; similar tracks don't need one. */}
                  {(t.relation === 'same_track' || removed) && (
                    <Chip
                      size="small"
                      label={removed ? 'Removed' : RELATION_LABELS[t.relation]}
                      color={t.relation === 'same_track' ? 'primary' : 'default'}
                      variant={t.relation === 'same_track' ? 'filled' : 'outlined'}
                    />
                  )}
                </Stack>
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                    {formatPct(t.weightShare)}
                  </Typography>
                  {/* Fixed-width action slot on every row (empty for the selected track), so percentages line up. */}
                  <Box sx={{ width: 24, flexShrink: 0, display: 'flex', justifyContent: 'center' }}>
                    {canRemove && (
                      <Tooltip title={removed ? 'Keep this track' : 'Remove this track'}>
                        <IconButton
                          className="track-action"
                          size="small"
                          onClick={() => onToggle(t.trackKey)}
                          aria-label={removed ? `Keep ${t.trackKey}` : `Remove ${t.trackKey}`}
                          sx={{ p: 0.25 }}
                        >
                          {removed ? <Undo fontSize="small" /> : <DeleteOutline fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </Stack>
              </Stack>
              <LinearProgress
                variant="determinate"
                value={Math.min(100, t.weightShare * 100)}
                sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
              />
              <Typography variant="caption" color="text.secondary">
                {twoLayer ? (
                  <>
                    {t.races} race{t.races === 1 ? '' : 's'}
                  </>
                ) : (
                  <>
                    {t.similarity !== null && `similarity ${t.similarity.toFixed(2)} · `}
                    {t.races} race{t.races === 1 ? '' : 's'} · weight {t.weight.toFixed(2)}
                  </>
                )}
              </Typography>
            </Box>
          );
        })}
      </Stack>

      {appliedExcluded.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Removed from this run
          </Typography>
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {appliedExcluded.map((k) => {
              const stillRemoved = pendingExcluded.has(k);
              return (
                <Stack key={k} direction="row" justifyContent="space-between" alignItems="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textDecoration: stillRemoved ? 'line-through' : 'none' }}
                    noWrap
                  >
                    {k}
                    {!stillRemoved && ' (restored on re-run)'}
                  </Typography>
                  <Tooltip title={stillRemoved ? 'Restore this track' : 'Keep it removed'}>
                    <IconButton size="small" onClick={() => onToggle(k)} sx={{ p: 0.25 }} aria-label={`Restore ${k}`}>
                      {stillRemoved ? <Undo fontSize="small" /> : <DeleteOutline fontSize="small" />}
                    </IconButton>
                  </Tooltip>
                </Stack>
              );
            })}
          </Stack>
        </Box>
      )}

      {(dirty || pendingExcluded.size > 0) && (
        <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
          {dirty && (
            <Button
              variant="contained"
              size="small"
              startIcon={isRunning ? <CircularProgress size={14} color="inherit" /> : <Replay />}
              onClick={onRerun}
              disabled={isRunning}
            >
              Re-run with {nKept} track{nKept === 1 ? '' : 's'}
            </Button>
          )}
          {pendingExcluded.size > 0 && (
            <Button size="small" onClick={onReset} disabled={isRunning}>
              Reset to all tracks
            </Button>
          )}
        </Stack>
      )}
    </Frame>
  );
};
